import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { UserPermissions, Modulo, PermissionType } from '../types/roles';
import { logger } from '../utils/logger';

export function usePermissions(userId: string | null, empresaId: string | null) {
  const [permissions, setPermissions] = useState<Map<string, UserPermissions>>(new Map());
  const [availableModules, setAvailableModules] = useState<Modulo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentRoleName, setCurrentRoleName] = useState<string | null>(null); // 'Admin', 'Contador', 'Empleado'
  const [isOwner, setIsOwner] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userId && empresaId) {
      loadPermissions();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, empresaId]);

  const loadPermissions = async () => {
    if (!userId || !empresaId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      logger.debug('🔍 Loading RBAC permissions for user:', userId, 'empresa:', empresaId);

      // Step 1: Check if user is owner of this empresa
      const { data: empresaOwner, error: _ownerError } = await supabase
        .from('empresas')
        .select('id, owner_id')
        .eq('id', empresaId)
        .maybeSingle();

      const isOwner = empresaOwner?.owner_id === userId;

      // Step 2: Get user's role for this empresa from usuarios_empresa table
      let empresaRole: string | null = null;
      if (!isOwner) {
        const { data: userEmpresaData, error: userEmpresaError } = await supabase
          .from('usuarios_empresa')
          .select('role')
          .eq('usuario_id', userId)
          .eq('empresa_id', empresaId)
          .maybeSingle();

        if (userEmpresaError) {
          logger.error('❌ Error loading user-empresa relationship:', userEmpresaError);
        } else {
          empresaRole = userEmpresaData?.role || null;
        }
      }

      // Step 3: Map empresa role to RBAC role name
      // usuarios_empresa.role → roles.nombre mapping:
      // 'owner' or 'admin' → 'Admin'
      // 'contador' → 'Contador'
      // 'empleado', 'vendedor', etc. → 'Empleado'
      let rbacRoleName: string;
      if (isOwner) {
        rbacRoleName = 'Admin';
      } else {
        const roleLower = empresaRole?.toLowerCase() || 'empleado';
        if (roleLower === 'contador') {
          rbacRoleName = 'Contador';
        } else if (roleLower === 'owner' || roleLower === 'admin') {
          rbacRoleName = 'Admin';
        } else {
          rbacRoleName = 'Empleado';
        }
      }

      // Store role info for Dashboard access check
      setIsOwner(isOwner);
      setCurrentRoleName(rbacRoleName);

      logger.debug('✅ Role mapping:', {
        isOwner,
        empresaRole,
        rbacRoleName,
        empresaId
      });

      // Step 4: Query roles table using empresa_id + RBAC role name
      const { data: roleData, error: roleError } = await supabase
        .from('roles')
        .select(`
          id,
          nombre,
          empresa_id,
          rol_permisos (
            modulo_id,
            puede_ver,
            puede_crear,
            puede_editar,
            puede_eliminar,
            modulos (
              id,
              nombre,
              descripcion,
              icono,
              ruta,
              orden,
              activo
            )
          )
        `)
        .eq('empresa_id', empresaId)
        .eq('nombre', rbacRoleName)
        .maybeSingle();

      if (roleError) {
        console.error('❌ Error loading role:', roleError);
        await loadFallbackPermissions(empresaId);
        return;
      }

      if (!roleData) {
        console.warn('⚠️ Role not found for empresa:', {
          empresaId,
          rbacRoleName,
          isOwner,
          empresaRole,
          message: 'Role might not exist for this empresa. Running migration might be needed.'
        });
        // Try to check if roles exist at all for this empresa
        const { data: allRoles, error: rolesError } = await supabase
          .from('roles')
          .select('id, nombre, empresa_id')
          .eq('empresa_id', empresaId);
        
        logger.debug('🔍 Available roles for empresa:', {
          roles: allRoles?.map(r => r.nombre),
          error: rolesError
        });
        
        await loadFallbackPermissions(empresaId);
        return;
      }

      logger.debug('✅ Role data loaded:', {
        hasRoleData: !!roleData,
        roleName: roleData.nombre,
        empresaId: roleData.empresa_id,
        permissionsCount: roleData.rol_permisos?.length || 0,
        permissions: roleData.rol_permisos?.map((p: any) => ({
          modulo_id: p.modulo_id,
          puede_ver: p.puede_ver,
          modulo: p.modulos?.nombre
        }))
      });

      // Step 3: Process role permissions if found
      if (roleData && roleData.rol_permisos) {
        const permisos = roleData.rol_permisos || [];
        logger.debug('✅ Found permissions:', permisos.length);
        
        const permissionsMap = new Map<string, UserPermissions>();
        const modules: Modulo[] = [];

        permisos.forEach((p: any) => {
          const m = p.modulos;
          if (p.puede_ver && m && m.activo !== false && m.id !== 'suscripciones') {
            const permiso: UserPermissions = {
              modulo_id: p.modulo_id,
              puede_ver: p.puede_ver,
              puede_crear: p.puede_crear,
              puede_editar: p.puede_editar,
              puede_eliminar: p.puede_eliminar,
              modulo: m
            };

            permissionsMap.set(p.modulo_id, permiso);
            modules.push(m);
          }
        });

        // Gastos: si existe en `modulos` pero falta en `rol_permisos` (migración no aplicada o empresa antigua),
        // heredar el mismo acceso que Facturas para que el menú y canAccess('gastos') funcionen.
        if (!permissionsMap.has('gastos')) {
          const facturasPerm = permissionsMap.get('facturas');
          if (facturasPerm?.puede_ver) {
            const { data: gastosModulo } = await supabase
              .from('modulos')
              .select('id, nombre, descripcion, icono, ruta, orden, activo')
              .eq('id', 'gastos')
              .eq('activo', true)
              .maybeSingle();
            if (gastosModulo) {
              logger.debug('📎 Inyectando módulo gastos desde permisos de facturas (rol sin fila explícita)');
              const permiso: UserPermissions = {
                modulo_id: 'gastos',
                puede_ver: facturasPerm.puede_ver,
                puede_crear: facturasPerm.puede_crear,
                puede_editar: facturasPerm.puede_editar,
                puede_eliminar: facturasPerm.puede_eliminar,
                modulo: gastosModulo as Modulo,
              };
              permissionsMap.set('gastos', permiso);
              modules.push(gastosModulo as Modulo);
            }
          }
        }

        if (!permissionsMap.has('cierre_caja')) {
          const facturasPerm = permissionsMap.get('facturas');
          if (facturasPerm?.puede_ver) {
            const { data: cierreModulo } = await supabase
              .from('modulos')
              .select('id, nombre, descripcion, icono, ruta, orden, activo')
              .eq('id', 'cierre_caja')
              .eq('activo', true)
              .maybeSingle();
            if (cierreModulo) {
              logger.debug('📎 Inyectando módulo cierre_caja desde permisos de facturas');
              const permiso: UserPermissions = {
                modulo_id: 'cierre_caja',
                puede_ver: facturasPerm.puede_ver,
                puede_crear: facturasPerm.puede_crear,
                puede_editar: facturasPerm.puede_editar,
                puede_eliminar: facturasPerm.puede_eliminar,
                modulo: cierreModulo as Modulo,
              };
              permissionsMap.set('cierre_caja', permiso);
              modules.push(cierreModulo as Modulo);
            }
          }
        }

        if (!permissionsMap.has('cobros')) {
          const facturasPerm = permissionsMap.get('facturas');
          if (facturasPerm?.puede_ver) {
            const { data: cobrosModulo } = await supabase
              .from('modulos')
              .select('id, nombre, descripcion, icono, ruta, orden, activo')
              .eq('id', 'cobros')
              .eq('activo', true)
              .maybeSingle();
            if (cobrosModulo) {
              logger.debug('📎 Inyectando módulo Cobros y Créditos (cobros) desde permisos de facturas');
              const permiso: UserPermissions = {
                modulo_id: 'cobros',
                puede_ver: facturasPerm.puede_ver,
                puede_crear: facturasPerm.puede_crear,
                puede_editar: facturasPerm.puede_editar,
                puede_eliminar: facturasPerm.puede_eliminar,
                modulo: cobrosModulo as Modulo,
              };
              permissionsMap.set('cobros', permiso);
              modules.push(cobrosModulo as Modulo);
            }
          }
        }

        if (modules.length > 0) {
          logger.debug('✅ Using RBAC permissions:', modules.length, 'modules');
          setPermissions(permissionsMap);
          setAvailableModules(modules.sort((a, b) => a.orden - b.orden));
          setLoading(false);
          return;
        } else {
          logger.warn('⚠️ Role found but no modules with puede_ver=true, falling back');
        }
      } else {
        logger.warn('⚠️ No role data or permissions found, falling back');
      }

      // Fallback: User doesn't have rol_id or RBAC setup
      // Show all modules as a temporary measure until RBAC is fully configured
      logger.warn('⚠️ User has no rol_id or RBAC permissions, loading fallback modules');
      await loadFallbackPermissions(empresaId);

    } catch (error) {
      logger.error('Error loading permissions:', error);
      // On error, show all modules so users aren't locked out
      await loadFallbackPermissions(empresaId);
    }
  };

  // Fallback: Load all active modules when RBAC is not configured
  const loadFallbackPermissions = async (empresaId: string) => {
    try {
      console.log('🔄 Loading fallback permissions for empresa:', empresaId);
      const { data: allModules, error: modulesError } = await supabase
        .from('modulos')
        .select('*')
        .eq('activo', true)
        .order('orden', { ascending: true });

      if (modulesError) {
        logger.error('❌ Error loading fallback modules:', modulesError);
        setError(modulesError.message);
        setLoading(false);
        return;
      }

      logger.debug('✅ Loaded fallback modules:', allModules?.length || 0);

      // Grant full permissions to all modules as fallback
      const permissionsMap = new Map<string, UserPermissions>();
      const modules: Modulo[] = [];

      allModules?.forEach((modulo) => {
        const permiso: UserPermissions = {
          modulo_id: modulo.id,
          puede_ver: true,
          puede_crear: true,
          puede_editar: true,
          puede_eliminar: true,
          modulo: modulo
        };
        permissionsMap.set(modulo.id, permiso);
        modules.push(modulo);
      });

      logger.debug('✅ Setting fallback permissions:', permissionsMap.size, 'modules:', modules.length);
      setPermissions(permissionsMap);
      setAvailableModules(modules);
    } catch (error) {
      logger.error('❌ Error in fallback permissions:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (moduloId: string, permiso: PermissionType): boolean => {
    const modulePermissions = permissions.get(moduloId);
    if (!modulePermissions) return false;

    switch (permiso) {
      case 'ver':
        return modulePermissions.puede_ver;
      case 'crear':
        return modulePermissions.puede_crear;
      case 'editar':
        return modulePermissions.puede_editar;
      case 'eliminar':
        return modulePermissions.puede_eliminar;
      default:
        return false;
    }
  };

  const canAccess = (moduloId: string): boolean => {
    // If permissions map is empty, allow access (fallback mode)
    if (permissions.size === 0) {
      return true;
    }
    return hasPermission(moduloId, 'ver');
  };

  // Check if user can access Dashboard (only Admin and Contador)
  const canAccessDashboard = (): boolean => {
    return currentRoleName === 'Admin' || currentRoleName === 'Contador';
  };

  return {
    permissions,
    availableModules,
    loading,
    error,
    hasPermission,
    canAccess,
    canAccessDashboard,
    currentRoleName,
    isOwner,
    refreshPermissions: loadPermissions
  };
}
